/**
 * 进度表格组件
 * 展示评分或进度的详细信息
 *
 * @component ProgressTable
 * @props {Object} progressStatus - 进度状态对象
 * @props {Boolean} progressStatus.background - 背景技术修改状态
 * @props {Boolean} progressStatus.problem - 技术问题修改状态
 * @props {Boolean} progressStatus.technical - 技术方案修改状态
 * @props {Boolean} progressStatus.effect - 有益效果修改状态
 * @props {Boolean} progressStatus.implementation - 实施方式修改状态
 * @props {Boolean} progressStatus.description - 简要说明修改状态
 */

<template>
  <div class="status-section">
    <div class="progress-table">
      <el-anchor>
        <el-anchor-link v-for="link in filteredInfoLinks" :key="link.title" :href="`#${link.key || link.title}`" @click="handleLink(link)">
          <div v-if="link.showProgress" class="progress-item">
            <div :class="{ 'progress-cell': true, 'show': true }">
              <el-icon :size="14">
                <svg-icon :name="getIconName(link.key)" />
              </el-icon>
              <span>{{ isFirstStep ? '填写' : '生成' }}</span>
            </div>
            <div class="progress-label">{{ link.title }}</div>
            <div class="progress-update" v-if="progressStatus[link.key]">
              <el-icon :size="14"><svg-icon name="edit" /></el-icon>
              <span>已修改</span>
            </div>
            <div class="progress-desc" v-if="link.desc">{{ link.desc }}</div>
          </div>
          <span v-else>{{ link.title }}</span>
        </el-anchor-link>
      </el-anchor>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  progressStatus: {
    type: Object,
    required: true
  },
  isFirstStep: {
    type: Boolean,
    default: false
  },
  isGenerating: {
    type: Boolean,
    default: false
  },
  generatingFields: {
    type: Object,
    default: () => ({
      background: false,
      problem: false,
      technical: false,
      effect: false,
      implementation: false,
      termInterpretation:false,
      description: false
    })
  },
  hasBackground: {
    type: Boolean,
    default: false
  },
  hasProblem: {
    type: Boolean,
    default: false
  },
  hasTechnical: {
    type: Boolean,
    default: false
  },
  hasEffect: {
    type: Boolean,
    default: false
  },
  hasImplementation: {
    type: Boolean,
    default: false
  },
  hasTermInterpretation: {
    type: Boolean,
    default: false
  },
  currentStep: {
    type: Number,
    default: 0
  },
  hasDescription: {
    type: Boolean,
    default: false
  }
})


const links = [
  { title: '基本信息', showProgress: false },
  { title: '技术信息', showProgress: false },
  { title: '背景技术', key: 'background', desc: '描述现有技术及其不足', showProgress: true },
  { title: '技术问题', key: 'problem', desc: '阐明需解决的具体问题', showProgress: true },
  { title: '技术方案', key: 'technical', desc: '提供问题的具体解决方案', showProgress: true },
  { title: '有益效果', key: 'effect', desc: '说明方案带来的技术优势', showProgress: true },
  { title: '实施方式', key: 'implementation', desc: '详细描述方案的实现步骤', showProgress: true },
  { title: '术语解释', key: 'termInterpretation', desc: '详细描述术语解释', showProgress: true },
  { title: '附图说明', key: 'description', desc: '配图解释方案的结构或流程', showProgress: true },
];

const filteredInfoLinks = computed(() => {
  return links.filter(item => {
    // 术语解释仅在第2步显示
    if (item.key === 'termInterpretation') {
      return props.currentStep === 2;
    }
    return true; // 其他字段正常显示
  });
})

const getIconName = (field) => {
  if (props.isFirstStep) {
    return props[`has${field.charAt(0).toUpperCase() + field.slice(1)}`] ? 'check' : 'check-none'
  }

  // 检查字段是否正在生成中
  if (props.generatingFields[field]) {
    return 'check-none'
  }

  // 检查字段内容
  return props[`has${field.charAt(0).toUpperCase() + field.slice(1)}`] ? 'check' : 'check-none'
}

const handleLink = (link) => {
  console.log(link)
}
</script>

<style scoped lang="scss">
.status-section {
  padding-bottom: 16px;
}

.progress-table {
  margin-top: 20px;
  :deep(.el-anchor) {
    .el-anchor__marker {
      height: 40px;
      z-index: 10;
    }
    .el-anchor__list {
      padding-left: 18px;
    }
    .el-anchor__link {
      padding: 8px 0;
      color: #222;
      font-size: 14px;
      &::before {
        position: absolute;
        content: '';
        left: 0;
        width: 2px;
        height: 100%;
        background: #EEEEEE;
      }
    }
  }
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  padding-bottom: 20px;
}

.progress-label {
  font-weight: 500;
}

.progress-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 52px;
  height: 22px;
  line-height: 22px;
  padding: 0 5px;
  color: #999;
  font-size: 12px;
  background: #F3F3F3;
  border-radius: 2px;
  &.show {
    color: var(--el-color-primary);
    background: rgba(0,161,96,0.06);
  }
}

.progress-update {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--el-color-primary);
  font-size: 12px;
}

.progress-desc {
  position: absolute;
  bottom: 0;
  left: 70px;
  color: #999;
  font-size: 12px;
}

.progress-modified {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 4px 8px;
  background: #10B981;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.3s ease;
  white-space: nowrap;
}

.progress-modified.show {
  opacity: 1;
}

:deep(.el-progress-circle) {
  width: 24px !important;
  height: 24px !important;
}

:deep(.el-progress-circle__path) {
  stroke: #10B981 !important;
}

:deep(.el-progress-circle__track) {
  stroke: #E2E8F0 !important;
}
</style>
